import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { LuCar } from 'react-icons/lu';
import { FaPlayCircle, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/utilityApiConfig';
import { logger } from '../../utils/logger';
import LoadingIndicator from '../../components/general/LoadingIndicator';
import { StopChainMap } from '../../components/utility_planning/StopChainMap';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import { getMdhdWorkflowConfig } from '../../utils/mdhdWorkflowConfig';
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

const FORECAST_YEARS = Array.from({ length: 24 }, (_, index) => 2027 + index);
const DEFAULT_EV_FORECAST = FORECAST_YEARS.map((year) => {
    const anchorValues = {
        2027: 1000,
        2028: 2500,
        2029: 4000,
        2030: 5000,
    };
    if (anchorValues[year] != null) return anchorValues[year];
    return 5000 + ((year - 2030) * 1000);
});

const EV_COUNT_MIN = 0;
const EV_COUNT_MAX = 100000;
const EV_COUNT_STEP = 10;
const SEASON_OPTIONS = [
    ['spring', 'Spring'],
    ['summer', 'Summer'],
    ['fall', 'Fall'],
    ['winter', 'Winter'],
];

function clampEvCount(value) {
    const n = Math.round((Math.max(0, parseInt(value, 10) || 0)) / EV_COUNT_STEP) * EV_COUNT_STEP;
    return Math.max(EV_COUNT_MIN, Math.min(EV_COUNT_MAX, n));
}

function formatBenchmarkSeconds(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return `${numeric.toFixed(3)}s`;
}

function formatBytes(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return '—';
    if (numeric < 1024) return `${numeric} B`;
    if (numeric < 1024 ** 2) return `${(numeric / 1024).toFixed(1)} KB`;
    if (numeric < 1024 ** 3) return `${(numeric / (1024 ** 2)).toFixed(1)} MB`;
    return `${(numeric / (1024 ** 3)).toFixed(2)} GB`;
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function jsonValuesEqual(leftValue, rightValue) {
    return JSON.stringify(leftValue ?? null) === JSON.stringify(rightValue ?? null);
}

function collectJsonDiffs(leftValue, rightValue, maxDiffs = 200) {
    const diffs = [];
    let truncated = false;

    function pushDiff(path, left, right) {
        if (diffs.length >= maxDiffs) {
            truncated = true;
            return;
        }
        diffs.push({
            path: path || '(root)',
            left,
            right,
        });
    }

    function visit(left, right, path) {
        if (truncated || jsonValuesEqual(left, right)) {
            return;
        }

        if (Array.isArray(left) && Array.isArray(right)) {
            if (left.length !== right.length) {
                pushDiff(path ? `${path}.length` : 'length', left.length, right.length);
            }
            const maxLength = Math.max(left.length, right.length);
            for (let index = 0; index < maxLength; index += 1) {
                visit(left[index], right[index], `${path}[${index}]`);
                if (truncated) return;
            }
            return;
        }

        if (isPlainObject(left) && isPlainObject(right)) {
            const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)])).sort();
            for (const key of keys) {
                visit(left[key], right[key], path ? `${path}.${key}` : key);
                if (truncated) return;
            }
            return;
        }

        pushDiff(path, left, right);
    }

    visit(leftValue ?? null, rightValue ?? null, '');
    return { diffs, truncated };
}

function formatDiffValue(value) {
    if (typeof value === 'string') return value;
    if (value == null) return 'null';
    return JSON.stringify(value, null, 2);
}

function getBenchmarkCandidateFallbackLabel(benchmarkType, benchmarkRunMode) {
    if (benchmarkRunMode === 'legacy_rust_pointer') return 'Rust pointer';
    if (benchmarkType === 'serial_parallel_test') return 'Python parallel';
    return 'Rust pointer';
}

function JsonDetails({ title, value, defaultOpen = false }) {
    return (
        <details className="rounded border border-gray-200 bg-white" open={defaultOpen}>
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-800 bg-gray-50">
                {title}
            </summary>
            <pre className="max-h-[420px] overflow-auto p-3 text-[11px] leading-5 text-gray-700 whitespace-pre-wrap break-all">
                {JSON.stringify(value ?? null, null, 2)}
            </pre>
        </details>
    );
}

function JsonPanel({ title, value }) {
    return (
        <div className="rounded border border-gray-200 bg-white overflow-hidden">
            <div className="px-3 py-2 text-sm font-medium text-gray-800 bg-gray-50 border-b border-gray-200">
                {title}
            </div>
            <pre className="max-h-[520px] overflow-auto p-3 text-[11px] leading-5 text-gray-700 whitespace-pre-wrap break-all">
                {JSON.stringify(value ?? null, null, 2)}
            </pre>
        </div>
    );
}

function SideBySideJsonComparison({ title, leftTitle, leftValue, rightTitle, rightValue, defaultOpen = false }) {
    return (
        <details className="rounded border border-gray-200 bg-white" open={defaultOpen}>
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-800 bg-gray-50">
                {title}
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-gray-50">
                <JsonPanel title={leftTitle} value={leftValue} />
                <JsonPanel title={rightTitle} value={rightValue} />
            </div>
        </details>
    );
}

function JsonDiffHighlighter({ title, leftTitle, leftValue, rightTitle, rightValue, defaultOpen = false }) {
    const { diffs, truncated } = useMemo(
        () => collectJsonDiffs(leftValue, rightValue, 200),
        [leftValue, rightValue],
    );

    return (
        <details className="rounded border border-amber-200 bg-amber-50" open={defaultOpen}>
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-amber-900 bg-amber-100">
                {title}
            </summary>
            <div className="p-3 space-y-3">
                {diffs.length === 0 ? (
                    <div className="rounded border border-green-200 bg-green-50 p-3 text-xs text-green-800">
                        No differences found in this view.
                    </div>
                ) : (
                    <>
                        <div className="text-xs text-amber-800">
                            {diffs.length} differing field{diffs.length === 1 ? '' : 's'} highlighted{truncated ? ' (truncated to first 200 differences)' : ''}.
                        </div>
                        {diffs.map((diff) => (
                            <div key={diff.path} className="rounded border border-amber-200 bg-white overflow-hidden">
                                <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 font-mono text-[11px] text-amber-900 break-all">
                                    {diff.path}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
                                    <div className="rounded border border-blue-200 bg-blue-50 overflow-hidden">
                                        <div className="px-3 py-1.5 text-xs font-semibold text-blue-900 border-b border-blue-200">
                                            {leftTitle}
                                        </div>
                                        <pre className="max-h-48 overflow-auto p-3 text-[11px] leading-5 text-blue-950 whitespace-pre-wrap break-all">
                                            {formatDiffValue(diff.left)}
                                        </pre>
                                    </div>
                                    <div className="rounded border border-rose-200 bg-rose-50 overflow-hidden">
                                        <div className="px-3 py-1.5 text-xs font-semibold text-rose-900 border-b border-rose-200">
                                            {rightTitle}
                                        </div>
                                        <pre className="max-h-48 overflow-auto p-3 text-[11px] leading-5 text-rose-950 whitespace-pre-wrap break-all">
                                            {formatDiffValue(diff.right)}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </details>
    );
}

const FORECAST_POINT_TYPE = 'FORECAST_POINT';
const PADDING = { top: 12, right: 12, bottom: 28, left: 44 };
const CHART_HEIGHT = 220;
const CHART_VIEW_WIDTH = 900;

function ForecastPoint({ index, cx, cy, color }) {
    const [{ opacity }, dragRef] = useDrag({
        type: FORECAST_POINT_TYPE,
        item: () => ({ index }),
        collect: (monitor) => ({
            opacity: monitor.isDragging() ? 0.5 : 1,
        }),
    });
    const r = 6;
    const hit = 14;
    return (
        <g>
            <circle
                cx={cx}
                cy={cy}
                r={r}
                fill={color}
                stroke="#fff"
                strokeWidth={2}
                opacity={opacity}
            />
            <foreignObject x={cx - hit} y={cy - hit} width={hit * 2} height={hit * 2} style={{ overflow: 'visible' }}>
                <div
                    ref={dragRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        cursor: 'ns-resize',
                        pointerEvents: 'all',
                    }}
                />
            </foreignObject>
        </g>
    );
}

function ForecastChartDnd({ values, onValueChange, years, label, color }) {
    const containerRef = useRef(null);
    const vals = values ?? [];
    const yMax = Math.max(...vals, 1) * 1.15;
    const innerWidth = CHART_VIEW_WIDTH - PADDING.left - PADDING.right;
    const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;

    const [, dropRef] = useDrop({
        accept: FORECAST_POINT_TYPE,
        hover(item, monitor) {
            const offset = monitor.getClientOffset();
            if (!offset || !containerRef.current || item?.index == null) return;
            const rect = containerRef.current.getBoundingClientRect();
            const mouseY = offset.y - rect.top;
            const chartTop = PADDING.top;
            const chartBottom = rect.height - PADDING.bottom;
            const innerH = chartBottom - chartTop;
            if (innerH <= 0) return;
            const t = 1 - (mouseY - chartTop) / innerH;
            const value = Math.round(yMax * t);
            const clamped = Math.max(0, Math.min(Math.ceil(yMax), value));
            onValueChange(item.index, String(clamped));
        },
    });

    const points = years.map((year, i) => {
        const v = Math.max(0, Number(vals[i]) || 0);
        const x = PADDING.left + (i / Math.max(1, years.length - 1)) * innerWidth;
        const y = PADDING.top + innerHeight - (v / yMax) * innerHeight;
        return { x, y, v, year, index: i };
    });

    const linePath = points.length > 0
        ? `M ${points.map((p) => `${p.x} ${p.y}`).join(' L ')}`
        : '';

    const gradId = `grad-${label}-${color.replace('#', '')}`;

    return (
        <div
            ref={(node) => {
                containerRef.current = node;
                dropRef(node);
            }}
            className="border rounded overflow-hidden bg-white w-full min-w-0"
            style={{ height: CHART_HEIGHT, width: '100%', minWidth: 0, maxWidth: '100%', display: 'block' }}
        >
            <svg width="100%" height="100%" viewBox={`0 0 ${CHART_VIEW_WIDTH} ${CHART_HEIGHT}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <text x={PADDING.left - 8} y={CHART_HEIGHT / 2} textAnchor="middle" transform={`rotate(-90, ${PADDING.left - 8}, ${CHART_HEIGHT / 2})`} fontSize={10} fill="#374151">
                    {label}
                </text>
                <line x1={PADDING.left} y1={PADDING.top + innerHeight} x2={PADDING.left + innerWidth} y2={PADDING.top + innerHeight} stroke="#e5e7eb" strokeDasharray="2 2" />
                <path d={linePath} fill="none" stroke={color} strokeWidth={2} />
                {points.length > 1 && (
                    <path
                        d={`${linePath} L ${points[points.length - 1].x} ${PADDING.top + innerHeight} L ${points[0].x} ${PADDING.top + innerHeight} Z`}
                        fill={`url(#${gradId})`}
                    />
                )}
                {points.map((p) => (
                    <ForecastPoint key={p.year} index={p.index} cx={p.x} cy={p.y} color={color} />
                ))}
                {points.map((p) => (
                    <text key={`label-${p.year}`} x={p.x} y={CHART_HEIGHT - 6} textAnchor="middle" fontSize={10} fill="#6b7280">
                        {p.year}
                    </text>
                ))}
            </svg>
        </div>
    );
}

function formatTime(t) {
    if (t == null) return '—';
    if (typeof t === 'string') return t.length > 20 ? t.slice(0, 19) : t;
    return String(t);
}

function formatPacificTime(t) {
    if (t == null) return '—';
    try {
        const date = new Date(t);
        if (Number.isNaN(date.getTime())) {
            return formatTime(t);
        }
        return new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short',
        }).format(date);
    } catch (error) {
        return formatTime(t);
    }
}

/** Format timestamp for chart axis: yyyy-mm-dd hh:mm:ss */
function formatTimeAxis(ts) {
    if (ts == null) return '';
    const s = typeof ts === 'string' ? ts : String(ts);
    const d = s.replace('T', ' ').trim();
    if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(d)) return d.slice(0, 19);
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10) + ' 00:00:00';
    return d.slice(0, 19);
}

/** Convert TOU segments to chart data: [{ hour, price }, ...] */
function touToChartData(tou) {
    if (!tou || !Array.isArray(tou) || tou.length === 0) return [];
    const data = [];
    let lastPrice = 0;
    for (let h = 0; h <= 24; h++) {
        let price = 0;
        for (let i = 0; i < tou.length; i++) {
            const p = tou[i];
            const start = Array.isArray(p) ? p[0] : p?.start;
            const end = Array.isArray(p) ? p[1] : p?.end;
            const val = Array.isArray(p) ? p[2] : p?.price;
            const inPeriod = start != null && end != null && (
                (h >= start && h < end) ||
                (h === 24 && end === 24 && start < 24)
            );
            if (inPeriod) {
                price = val != null ? Number(val) : 0;
                break;
            }
        }
        if (h === 24 && price === 0) price = lastPrice;
        lastPrice = price;
        data.push({ hour: h, price });
    }
    return data;
}

function formatTouPeriodLabel(tou) {
    if (!Array.isArray(tou) || tou.length === 0) return 'No TOU schedule';
    return tou
        .map((period) => {
            const start = Array.isArray(period) ? period[0] : period?.start;
            const end = Array.isArray(period) ? period[1] : period?.end;
            const price = Array.isArray(period) ? period[2] : period?.price;
            if (start == null || end == null || price == null) return null;
            return `${start}-${end}:$${Number(price).toFixed(4)}`;
        })
        .filter(Boolean)
        .join(' | ');
}

function normalizeUtilityName(name) {
    return String(name || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function utilitiesEquivalent(a, b) {
    const na = normalizeUtilityName(a);
    const nb = normalizeUtilityName(b);
    if (!na || !nb) return false;
    if (na === nb) return true;

    const aliasToCanonical = {
        // SCE
        sce: 'southerncaliforniaedison',
        southerncaliforniaedison: 'southerncaliforniaedison',
        southerncaliforniaedisonco: 'southerncaliforniaedison',
        // LADWP
        ladwp: 'losangelesdepartmentofwaterpower',
        losangelesdepartmentofwaterpower: 'losangelesdepartmentofwaterpower',
        // PG&E
        pge: 'pacificgasandelectric',
        pacificgasandelectric: 'pacificgasandelectric',
        pacificgasandelectricco: 'pacificgasandelectric',
        // SDG&E
        sdge: 'sandiegogasandelectric',
        sandiegogasandelectric: 'sandiegogasandelectric',
        sandiegogasandelectricco: 'sandiegogasandelectric',
    };

    const ca = aliasToCanonical[na] || na;
    const cb = aliasToCanonical[nb] || nb;
    return ca === cb;
}

function RowDetailContent({ row }) {
    const [detailTab, setDetailTab] = useState('overview');
    const [energyComparisonView, setEnergyComparisonView] = useState('both');
    const socProfile = row.soc_profile || [];
    const totalCost = row.total_cost;
    const chargingProfiles = row.charging_profiles || [];
    const chargingStopTou = row.charging_stop_tou || [];
    const stopWeather = row.stop_weather || [];
    const driveEnergyComparison = row.drive_energy_comparison || [];
    const driveEnergySummary = row.drive_energy_summary || null;

    // Top chart: SoC only (from full trip soc_profile)
    const socChartData = useMemo(() => {
        return socProfile
            .map((p) => {
                const t = p.time == null ? null : (typeof p.time === 'string' ? p.time : String(p.time));
                if (t == null) return null;
                return {
                    time: t,
                    timeLabel: formatTimeAxis(t),
                    soc: p.soc != null ? Number(p.soc) : null,
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.time.localeCompare(b.time));
    }, [socProfile]);

    const hasSocChart = socChartData.length > 0 && socChartData.some((d) => d.soc != null);
    const weatherChartData = useMemo(() => {
        return stopWeather
            .map((entry) => ({
                stopLabel: `Stop ${Number(entry?.stop_index ?? 0) + 1}`,
                representativeTemperatureC: entry?.representative_temperature_c != null ? Number(entry.representative_temperature_c) : null,
                segmentAvgTempToNextC: entry?.segment_avg_temp_to_next_c != null ? Number(entry.segment_avg_temp_to_next_c) : null,
            }));
    }, [stopWeather]);
    const driveEnergyChartData = useMemo(() => {
        return driveEnergyComparison.map((entry) => ({
            segmentLabel: entry?.segment_label || `Segment ${Number(entry?.segment_index ?? 0) + 1}`,
            idealEnergyKwh: entry?.ideal_energy_kwh != null ? Number(entry.ideal_energy_kwh) : null,
            weatherEnergyKwh: entry?.weather_energy_kwh != null ? Number(entry.weather_energy_kwh) : null,
            deltaEnergyKwh: entry?.delta_energy_kwh != null ? Number(entry.delta_energy_kwh) : null,
            ambientTempC: entry?.ambient_temp_c != null ? Number(entry.ambient_temp_c) : null,
            efficiencyFactor: entry?.efficiency_factor != null ? Number(entry.efficiency_factor) : null,
        }));
    }, [driveEnergyComparison]);

    return (
        <div className="space-y-4 py-2">
            <div className="flex gap-2 border-b border-gray-200 pb-2">
                {[
                    ['overview', 'Overview'],
                    ['charging', 'Charging'],
                    ['weather', 'Weather'],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setDetailTab(key)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                            detailTab === key
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {detailTab === 'overview' && (
                <>
                    <section>
                        <h4 className="text-xs font-semibold text-gray-700 mb-1">Total cost</h4>
                        <p className="text-lg font-bold text-gray-900">${totalCost != null ? Number(totalCost).toFixed(2) : '—'}</p>
                    </section>
                    <section>
                        <h4 className="text-xs font-semibold text-gray-700 mb-1">State of charge over time</h4>
                        {hasSocChart ? (
                            <div className="border rounded overflow-hidden bg-white">
                                <ResponsiveContainer width="100%" height={280}>
                                    <ComposedChart
                                        data={socChartData}
                                        margin={{ top: 8, right: 16, left: 0, bottom: 24 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis
                                            dataKey="timeLabel"
                                            tick={{ fontSize: 10 }}
                                            interval="preserveStartEnd"
                                            angle={-25}
                                            textAnchor="end"
                                            height={48}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 10 }}
                                            domain={[0, 1]}
                                            tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                                            label={{ value: 'SoC', angle: -90, position: 'insideLeft', fontSize: 10 }}
                                        />
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const p = payload[0]?.payload;
                                                if (!p) return null;
                                                return (
                                                    <div className="bg-white border border-gray-200 rounded shadow-lg px-3 py-2 text-xs">
                                                        <p className="font-semibold text-gray-800 mb-1">{p.timeLabel}</p>
                                                        {p.soc != null && <p>SoC: {(p.soc * 100).toFixed(2)}%</p>}
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 10 }} />
                                        <Line
                                            type="monotone"
                                            dataKey="soc"
                                            name="State of charge"
                                            stroke="#2563eb"
                                            strokeWidth={2}
                                            dot={false}
                                            connectNulls
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="border rounded py-6 text-center text-gray-500 text-sm">No SoC data to graph</div>
                        )}
                    </section>
                </>
            )}
            {detailTab === 'charging' && (
                <>
                    <section>
                <h4 className="text-xs font-semibold text-gray-700 mb-1">Charging profile by stop</h4>
                {chargingProfiles.length === 0 ? (
                    <div className="border rounded py-4 text-center text-gray-500 text-sm">No charging profile data</div>
                ) : (
                    <div className="space-y-6">
                        {chargingProfiles.map((stopProfile, stopIdx) => {
                            const stopChartData = Array.isArray(stopProfile)
                                ? stopProfile.map((seg) => ({
                                      time: seg.time == null ? null : (typeof seg.time === 'string' ? seg.time : String(seg.time)),
                                      timeLabel: formatTimeAxis(seg.time),
                                      soc: seg.soc != null ? Number(seg.soc) : null,
                                      chargingPower: seg.charging_power != null ? Number(seg.charging_power) : 0,
                                      energyFromCharger: seg.energy_from_charger != null ? Number(seg.energy_from_charger) : null,
                                      mode: seg.mode ?? '',
                                  }))
                                : [];
                            const hasStopData = stopChartData.length > 0 && stopChartData.some((d) => d.soc != null || d.chargingPower > 0 || d.energyFromCharger != null);
                            const stopTouInfo = chargingStopTou[stopIdx];
                            const stopTouChartData = stopTouInfo?.tou ? touToChartData(stopTouInfo.tou) : [];
                            const stopUtilityLabel = stopTouInfo?.utility_region || 'Unknown utility';
                            const stopRateName = stopTouInfo?.tou_rate_name || null;
                            const stopRateSector = stopTouInfo?.tou_rate_sector || null;
                            const stopRateUtilityName = stopTouInfo?.tou_rate_utility_name || null;
                            const stopTouIsDefault = Boolean(stopTouInfo?.tou_is_default);
                            return (
                                <div key={stopIdx} className="border rounded overflow-hidden bg-white">
                                    <div className="bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 border-b border-gray-200 flex justify-between items-center">
                                        <span>Stop {stopIdx + 1}</span>
                                        {stopTouInfo && (
                                            <span className="text-gray-600 font-normal">
                                                TOU utility: {stopUtilityLabel}
                                            </span>
                                        )}
                                    </div>
                                    {hasStopData ? (
                                        <>
                                            <ResponsiveContainer width="100%" height={240}>
                                                <ComposedChart
                                                    data={stopChartData}
                                                    margin={{ top: 8, right: 30, left: 0, bottom: 24 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                    <XAxis
                                                        dataKey="timeLabel"
                                                        tick={{ fontSize: 10 }}
                                                        interval="preserveStartEnd"
                                                        angle={-25}
                                                        textAnchor="end"
                                                        height={48}
                                                    />
                                                    <YAxis
                                                        yAxisId="soc"
                                                        orientation="left"
                                                        tick={{ fontSize: 10 }}
                                                        domain={[0, 1]}
                                                        tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                                                        label={{ value: 'SoC', angle: -90, position: 'insideLeft', fontSize: 10 }}
                                                    />
                                                    <YAxis
                                                        yAxisId="power"
                                                        orientation="right"
                                                        tick={{ fontSize: 10 }}
                                                        tickFormatter={(v) => `${v}`}
                                                        label={{ value: 'Power (kW) / Energy (kWh)', angle: 90, position: 'insideRight', fontSize: 10 }}
                                                    />
                                                    <Tooltip
                                                        content={({ active, payload }) => {
                                                            if (!active || !payload?.length) return null;
                                                            const p = payload[0]?.payload;
                                                            if (!p) return null;
                                                            return (
                                                                <div className="bg-white border border-gray-200 rounded shadow-lg px-3 py-2 text-xs">
                                                                    <p className="font-semibold text-gray-800 mb-1">{p.timeLabel}</p>
                                                                    {p.soc != null && <p>SoC: {(p.soc * 100).toFixed(2)}%</p>}
                                                                    <p>Charging power: {p.chargingPower != null ? Number(p.chargingPower).toFixed(1) : '—'} kW</p>
                                                                    <p>Energy from charger: {p.energyFromCharger != null ? Number(p.energyFromCharger).toFixed(2) : '—'} kWh</p>
                                                                    {p.mode && <p className="text-blue-600 font-medium">Mode: {p.mode}</p>}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                                    <Line
                                                        yAxisId="soc"
                                                        type="monotone"
                                                        dataKey="soc"
                                                        name="State of charge"
                                                        stroke="#2563eb"
                                                        strokeWidth={2}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                    <Line
                                                        yAxisId="power"
                                                        type="stepBefore"
                                                        dataKey="chargingPower"
                                                        name="Charging power (kW)"
                                                        stroke="#10b981"
                                                        strokeWidth={2}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                    <Line
                                                        yAxisId="power"
                                                        type="monotone"
                                                        dataKey="energyFromCharger"
                                                        name="Energy from charger (kWh)"
                                                        stroke="#f59e0b"
                                                        strokeWidth={2}
                                                        dot={false}
                                                        connectNulls
                                                    />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                            {stopTouChartData.length > 0 && (
                                                <div className="border-t border-gray-200 px-3 py-2 bg-gray-50">
                                                    <p className="text-xs font-medium text-gray-700 mb-1">
                                                        TOU schedule
                                                        {stopRateName ? `: ${stopRateName}` : ''}
                                                        {stopRateSector ? ` (${stopRateSector})` : ''}
                                                        {stopTouIsDefault ? ' [DEFAULT FLAT RATE]' : ''}
                                                    </p>
                                                    <p className="text-[11px] text-gray-600 mb-1">
                                                        Utility region: {stopUtilityLabel}
                                                        {stopRateUtilityName ? ` | Rate utility: ${stopRateUtilityName}` : ''}
                                                        {stopTouInfo?.tou_rate_id ? ` | Rate ID: ${stopTouInfo.tou_rate_id}` : ''}
                                                    </p>
                                                    {stopTouInfo?.tou_selection_path && (
                                                        <p className="text-[11px] text-gray-500 mb-1">
                                                            Selection path: {stopTouInfo.tou_selection_path}
                                                            {stopTouInfo?.tou_candidate_count != null ? ` | Candidates: ${stopTouInfo.tou_candidate_count}` : ''}
                                                        </p>
                                                    )}
                                                    <ResponsiveContainer width="100%" height={80}>
                                                        <ComposedChart data={stopTouChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                                            <XAxis dataKey="hour" type="number" domain={[0, 24]} tick={{ fontSize: 8 }} tickCount={7} />
                                                            <YAxis tick={{ fontSize: 8 }} tickFormatter={(v) => `$${v}`} />
                                                            <Line type="stepBefore" dataKey="price" stroke="#7c3aed" strokeWidth={1.5} dot={false} />
                                                        </ComposedChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="py-4 text-center text-gray-500 text-xs">No data for this stop</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                    </section>
                    <section>
                <h4 className="text-xs font-semibold text-gray-700 mb-1">Charging profile (table)</h4>
                <div className="border rounded max-h-[180px] overflow-auto">
                    {chargingProfiles.map((stopProfile, stopIdx) => {
                        const touInfo = chargingStopTou[stopIdx];
                        return (
                        <div key={stopIdx} className="border-b border-gray-200 last:border-b-0">
                            <div className="bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 flex justify-between">
                                <span>Stop {stopIdx + 1}</span>
                                <span className="text-gray-600 font-normal">
                                    Utility: {touInfo?.utility_region || '—'}
                                    {touInfo?.tou_rate_name ? ` | TOU: ${touInfo.tou_rate_name}` : ''}
                                    {touInfo?.tou_is_default ? ' | DEFAULT' : ''}
                                </span>
                            </div>
                            <table className="min-w-full divide-y divide-gray-100 text-xs">
                                <thead>
                                    <tr>
                                        <th className="px-2 py-0.5 text-left text-gray-600">Time</th>
                                        <th className="px-2 py-0.5 text-right text-gray-600">SoC</th>
                                        <th className="px-2 py-0.5 text-right text-gray-600">Power</th>
                                        <th className="px-2 py-0.5 text-right text-gray-600">Energy</th>
                                        <th className="px-2 py-0.5 text-left text-gray-600">Mode</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(stopProfile) && stopProfile.map((seg, segIdx) => (
                                        <tr key={segIdx}>
                                            <td className="px-2 py-0.5 font-mono text-gray-700">{formatTime(seg.time)}</td>
                                            <td className="px-2 py-0.5 text-right">{seg.soc != null ? Number(seg.soc).toFixed(3) : '—'}</td>
                                            <td className="px-2 py-0.5 text-right">{seg.charging_power != null ? Number(seg.charging_power).toFixed(1) : '—'}</td>
                                            <td className="px-2 py-0.5 text-right">{seg.energy_from_charger != null ? Number(seg.energy_from_charger).toFixed(2) : '—'}</td>
                                            <td className="px-2 py-0.5 text-gray-600">{seg.mode ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        );
                    })}
                    {chargingProfiles.length === 0 && <div className="px-2 py-2 text-gray-500 text-center text-xs">No charging profile data</div>}
                </div>
                    </section>
                </>
            )}
            {detailTab === 'weather' && (
                <section className="space-y-4">
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">Weather by stop</h4>
                    {stopWeather.length === 0 ? (
                        <div className="border rounded py-4 text-center text-gray-500 text-sm">No weather data available for this route.</div>
                    ) : (
                        <>
                            {driveEnergySummary && (
                                <div className="space-y-3">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <h5 className="text-xs font-semibold text-gray-700">Drive energy comparison</h5>
                                            <p className="text-[11px] text-gray-500">Compare ideal weather (`efficiency = 1`) against weather-adjusted segment energy.</p>
                                        </div>
                                        <div className="flex gap-2">
                                            {[
                                                ['both', 'Both'],
                                                ['ideal', 'Ideal only'],
                                                ['weather', 'Weather only'],
                                            ].map(([key, label]) => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => setEnergyComparisonView(key)}
                                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                                                        energyComparisonView === key
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                            <div className="text-[11px] text-gray-500 uppercase">Ideal total</div>
                                            <div className="text-sm font-semibold text-gray-800">
                                                {driveEnergySummary.ideal_total_energy_kwh != null ? `${Number(driveEnergySummary.ideal_total_energy_kwh).toFixed(2)} kWh` : '—'}
                                            </div>
                                        </div>
                                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                            <div className="text-[11px] text-gray-500 uppercase">Weather total</div>
                                            <div className="text-sm font-semibold text-gray-800">
                                                {driveEnergySummary.weather_total_energy_kwh != null ? `${Number(driveEnergySummary.weather_total_energy_kwh).toFixed(2)} kWh` : '—'}
                                            </div>
                                        </div>
                                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                            <div className="text-[11px] text-gray-500 uppercase">Delta</div>
                                            <div className="text-sm font-semibold text-gray-800">
                                                {driveEnergySummary.delta_total_energy_kwh != null ? `${Number(driveEnergySummary.delta_total_energy_kwh).toFixed(2)} kWh` : '—'}
                                            </div>
                                        </div>
                                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                            <div className="text-[11px] text-gray-500 uppercase">Delta %</div>
                                            <div className="text-sm font-semibold text-gray-800">
                                                {driveEnergySummary.delta_total_pct != null ? `${Number(driveEnergySummary.delta_total_pct).toFixed(2)}%` : '—'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border rounded overflow-hidden bg-white">
                                        <ResponsiveContainer width="100%" height={240}>
                                            <ComposedChart data={driveEnergyChartData} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                <XAxis dataKey="segmentLabel" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={52} />
                                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(value) => Number(value).toFixed(2)} label={{ value: 'Energy (kWh)', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                                                <Tooltip
                                                    content={({ active, payload }) => {
                                                        if (!active || !payload?.length) return null;
                                                        const p = payload[0]?.payload;
                                                        if (!p) return null;
                                                        return (
                                                            <div className="bg-white border border-gray-200 rounded shadow-lg px-3 py-2 text-xs">
                                                                <p className="font-semibold text-gray-800 mb-1">{p.segmentLabel}</p>
                                                                {p.idealEnergyKwh != null && <p>Ideal: {Number(p.idealEnergyKwh).toFixed(2)} kWh</p>}
                                                                {p.weatherEnergyKwh != null && <p>Weather: {Number(p.weatherEnergyKwh).toFixed(2)} kWh</p>}
                                                                {p.deltaEnergyKwh != null && <p>Delta: {Number(p.deltaEnergyKwh).toFixed(2)} kWh</p>}
                                                                {p.ambientTempC != null && <p>Avg temp: {Number(p.ambientTempC).toFixed(2)} C</p>}
                                                                {p.efficiencyFactor != null && <p>Efficiency factor: {Number(p.efficiencyFactor).toFixed(2)}</p>}
                                                            </div>
                                                        );
                                                    }}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 10 }} />
                                                {(energyComparisonView === 'both' || energyComparisonView === 'ideal') && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="idealEnergyKwh"
                                                        name="Ideal weather"
                                                        stroke="#10b981"
                                                        strokeWidth={2}
                                                        dot={{ r: 2 }}
                                                        connectNulls
                                                    />
                                                )}
                                                {(energyComparisonView === 'both' || energyComparisonView === 'weather') && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="weatherEnergyKwh"
                                                        name="Weather-adjusted"
                                                        stroke="#2563eb"
                                                        strokeWidth={2}
                                                        dot={{ r: 2 }}
                                                        connectNulls
                                                    />
                                                )}
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}
                            <div className="border rounded overflow-hidden bg-white">
                                <ResponsiveContainer width="100%" height={240}>
                                    <ComposedChart data={weatherChartData} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="stopLabel" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={48} />
                                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(value) => Number(value).toFixed(2)} label={{ value: 'Temp (C)', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                                        <Tooltip
                                            formatter={(value) => (value != null ? Number(value).toFixed(2) : '—')}
                                            labelFormatter={(label) => label}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 10 }} />
                                        <Line
                                            type="monotone"
                                            dataKey="representativeTemperatureC"
                                            name="Arrival temp"
                                            stroke="#dc2626"
                                            strokeWidth={2}
                                            dot={{ r: 2 }}
                                            connectNulls
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="segmentAvgTempToNextC"
                                            name="Avg temp to next stop"
                                            stroke="#2563eb"
                                            strokeWidth={2}
                                            dot={{ r: 2 }}
                                            connectNulls
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="border rounded max-h-[320px] overflow-auto bg-white">
                                <div className="px-3 py-2 border-b border-gray-200 bg-blue-50 text-[11px] text-blue-900">
                                    Times in this table are shown in Pacific time. Arrival temp and departure temp are for the current stop. Avg temp to next stop is the average of this stop&apos;s departure temperature and the next stop&apos;s arrival temperature.
                                </div>
                                <table className="min-w-full divide-y divide-gray-200 text-xs">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 uppercase">Stop</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 uppercase">Arrival (PT)</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 uppercase">Departure (PT)</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 uppercase">Utility</th>
                                            <th className="px-2 py-1 text-right font-medium text-gray-600 uppercase">Arrival Temp (C)</th>
                                            <th className="px-2 py-1 text-right font-medium text-gray-600 uppercase">Departure Temp (C)</th>
                                            <th className="px-2 py-1 text-right font-medium text-gray-600 uppercase">Avg Temp to Next Stop (C)</th>
                                            <th className="px-2 py-1 text-right font-medium text-gray-600 uppercase">Next Stop Arrival Temp (C)</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 uppercase">Station</th>
                                            <th className="px-2 py-1 text-right font-medium text-gray-600 uppercase">Distance (km)</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 uppercase">Timezone</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {stopWeather.map((entry) => (
                                            <tr key={`weather-${entry.stop_index}`}>
                                                <td className="px-2 py-1 text-gray-800">{Number(entry.stop_index) + 1}</td>
                                                <td className="px-2 py-1 font-mono text-gray-700">{formatPacificTime(entry.arrival_time)}</td>
                                                <td className="px-2 py-1 font-mono text-gray-700">{formatPacificTime(entry.departure_time)}</td>
                                                <td className="px-2 py-1 text-gray-700">{entry.utility_region || '—'}</td>
                                                <td className="px-2 py-1 text-right text-gray-800">
                                                    {entry.arrival_temperature_c != null ? Number(entry.arrival_temperature_c).toFixed(1) : '—'}
                                                </td>
                                                <td className="px-2 py-1 text-right text-gray-800">
                                                    {entry.departure_temperature_c != null ? Number(entry.departure_temperature_c).toFixed(1) : '—'}
                                                </td>
                                                <td className="px-2 py-1 text-right text-gray-800">
                                                    {entry.segment_avg_temp_to_next_c != null ? Number(entry.segment_avg_temp_to_next_c).toFixed(1) : '—'}
                                                </td>
                                                <td className="px-2 py-1 text-right text-gray-800">
                                                    {entry.segment_end_temp_to_next_c != null ? Number(entry.segment_end_temp_to_next_c).toFixed(1) : '—'}
                                                </td>
                                                <td className="px-2 py-1 text-gray-700">
                                                    <div>{entry.station_name || entry.station_id || '—'}</div>
                                                    {entry.station_id && entry.station_name && (
                                                        <div className="text-gray-500">{entry.station_id}</div>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1 text-right text-gray-700">
                                                    {entry.station_distance_km != null ? Number(entry.station_distance_km).toFixed(1) : '—'}
                                                </td>
                                                <td className="px-2 py-1 text-gray-700">{entry.station_timezone || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </section>
            )}
        </div>
    );
}

function UtilityVehicleSimulation() {
    const location = useLocation();
    const mdhdWorkflowConfig = useMemo(() => getMdhdWorkflowConfig(), []);
        const [mode, setMode] = useState(() => {
            const savedMode = mdhdWorkflowConfig?.managedCharging?.v2gMode;
            if (savedMode === 'active_managed') return 'active';
            if (savedMode === 'passive_managed') return 'passive';
            return 'unmanaged';
        });
    const [touRatePreference, setTouRatePreference] = useState('ev_charging');
    const [depotChargingPower, setDepotChargingPower] = useState(() => mdhdWorkflowConfig?.managedCharging?.depotChargingPower ?? 50);
    const [enrouteChargingPower, setEnrouteChargingPower] = useState(() => mdhdWorkflowConfig?.managedCharging?.enrouteChargingPower ?? 350);
    const [evForecastByYear, setEvForecastByYear] = useState(() => [...DEFAULT_EV_FORECAST]);
    const [socMax, setSocMax] = useState(() => mdhdWorkflowConfig?.vehicleConfiguration?.socMax ?? 0.95);
    const [socMin, setSocMin] = useState(() => mdhdWorkflowConfig?.vehicleConfiguration?.socMin ?? 0.10);
    const [socThres, setSocThres] = useState(() => mdhdWorkflowConfig?.vehicleConfiguration?.socThreshold ?? 0.30);
    const [socInitial, setSocInitial] = useState(() => mdhdWorkflowConfig?.vehicleConfiguration?.socInitial ?? 0.95);
    const [selectedUtilities, setSelectedUtilities] = useState([]);
    const [selectedSeasons, setSelectedSeasons] = useState(() => SEASON_OPTIONS.map(([value]) => value));
    const [utilitySearchQuery, setUtilitySearchQuery] = useState('');
    const [utilitySearchResults, setUtilitySearchResults] = useState([]);
    const [utilitySearchLoading, setUtilitySearchLoading] = useState(false);
    const [mdhdForecastUtilities, setMdhdForecastUtilities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [simulationResults, setSimulationResults] = useState(null);
    const [error, setError] = useState(null);
    const [benchmarkLoading, setBenchmarkLoading] = useState(false);
    const [benchmarkError, setBenchmarkError] = useState(null);
    const [benchmarkResults, setBenchmarkResults] = useState(null);
    const [benchmarkRunMode, setBenchmarkRunMode] = useState('legacy_benchmark');
    const [benchmarkYearLimit, setBenchmarkYearLimit] = useState(1);
    const [benchmarkWeekLimit, setBenchmarkWeekLimit] = useState(4);
    const [benchmarkParallelWorkers, setBenchmarkParallelWorkers] = useState(() => {
        if (typeof navigator !== 'undefined' && Number.isFinite(Number(navigator.hardwareConcurrency))) {
            return Math.max(1, Number(navigator.hardwareConcurrency) - 1);
        }
        return 4;
    });
    const [expandedRowIndex, setExpandedRowIndex] = useState(null);
    const [showResultsPage, setShowResultsPage] = useState(false);
    const [selectedMapRowIndex, setSelectedMapRowIndex] = useState(0);
    const [selectedForecastYear, setSelectedForecastYear] = useState('');
    const [simulationElapsedSec, setSimulationElapsedSec] = useState(0);
    const [showChargersOnMap, setShowChargersOnMap] = useState(false);
    const [chargingStations, setChargingStations] = useState([]);
    const [chargingStationsLoading, setChargingStationsLoading] = useState(false);
    const [showLoadHeatmap, setShowLoadHeatmap] = useState(true);
    const [heatmapLayerMetric, setHeatmapLayerMetric] = useState('energy_delivered');
    const benchmarkCandidateFallbackLabel = useMemo(
        () => getBenchmarkCandidateFallbackLabel(benchmarkResults?.benchmark_type, benchmarkRunMode),
        [benchmarkResults?.benchmark_type, benchmarkRunMode],
    );
    const [cardRef, isCardVisible] = useScrollAnimation({ threshold: 0.2, triggerOnce: true });

    useEffect(() => {
        const preloadedResults = location.state?.preloadedSimulationResults;
        const preloadedRequest = location.state?.preloadedSimulationRequest;
        if (!preloadedResults) return;

        if (preloadedRequest) {
            if (preloadedRequest.mode) setMode(preloadedRequest.mode);
            if (preloadedRequest.soc_max != null) setSocMax(preloadedRequest.soc_max);
            if (preloadedRequest.soc_min != null) setSocMin(preloadedRequest.soc_min);
            if (preloadedRequest.soc_thres != null) setSocThres(preloadedRequest.soc_thres);
            if (preloadedRequest.soc_initial != null) setSocInitial(preloadedRequest.soc_initial);
            if (preloadedRequest.depot_charging_power != null) {
                setDepotChargingPower(preloadedRequest.depot_charging_power);
            } else if (preloadedRequest.charging_power != null) {
                setDepotChargingPower(preloadedRequest.charging_power);
            }
            if (preloadedRequest.enroute_charging_power != null) {
                setEnrouteChargingPower(preloadedRequest.enroute_charging_power);
            } else if (preloadedRequest.charging_power != null) {
                setEnrouteChargingPower(preloadedRequest.charging_power);
            }
            if (preloadedRequest.utility_filter) setSelectedUtilities(Array.isArray(preloadedRequest.utility_filter) ? preloadedRequest.utility_filter : []);
            if (preloadedRequest.season_filter) {
                setSelectedSeasons(Array.isArray(preloadedRequest.season_filter) ? preloadedRequest.season_filter : []);
            }
            if (preloadedRequest.ev_forecast) {
                const nextForecast = FORECAST_YEARS.map((year, idx) => {
                    const fallback = DEFAULT_EV_FORECAST[idx];
                    const raw = preloadedRequest.ev_forecast?.[year];
                    return Number.isFinite(Number(raw)) ? clampEvCount(raw) : fallback;
                });
                setEvForecastByYear(nextForecast);
            }
        }

        setSimulationResults(preloadedResults);
        const snapshots = Array.isArray(preloadedResults?.year_snapshots) ? preloadedResults.year_snapshots : [];
        if (snapshots.length > 0) {
            const finalSnapshot = snapshots[snapshots.length - 1];
            setSelectedForecastYear(String(finalSnapshot?.year ?? ''));
        } else {
            setSelectedForecastYear('');
        }
        if (preloadedResults?.results?.length > 0) {
            setShowResultsPage(true);
            setSelectedMapRowIndex(0);
            setExpandedRowIndex(0);
        }
    }, [location.state, setSelectedUtilities]);

    const touUsageRows = useMemo(() => {
        const rows = simulationResults?.results;
        if (!Array.isArray(rows) || rows.length === 0) return [];
        const byKey = new Map();
        rows.forEach((row) => {
            const stops = Array.isArray(row?.charging_stop_tou) ? row.charging_stop_tou : [];
            stops.forEach((s) => {
                const utility = s?.utility_region || s?.tou_rate_utility_name || 'Unknown utility';
                const sourceUtility = s?.tou_rate_utility_name || null;
                const touName = s?.tou_rate_name || (s?.tou_is_default ? 'DEFAULT_TOU' : 'Mapped TOU');
                const tou = Array.isArray(s?.tou) ? s.tou : [];
                const key = `${utility}|||${sourceUtility || ''}|||${touName}|||${formatTouPeriodLabel(tou)}`;
                if (!byKey.has(key)) {
                    byKey.set(key, {
                        utility,
                        sourceUtility,
                        touName,
                        tou,
                        chartData: touToChartData(tou),
                        count: 0,
                    });
                }
                byKey.get(key).count += 1;
            });
        });
        return Array.from(byKey.values()).sort((a, b) => {
            if (a.utility !== b.utility) return a.utility.localeCompare(b.utility);
            return a.touName.localeCompare(b.touName);
        });
    }, [simulationResults?.results]);

    const selectedRouteTouRows = useMemo(() => {
        const rows = simulationResults?.results;
        if (!Array.isArray(rows) || rows.length === 0) return [];
        const selected = rows[selectedMapRowIndex];
        if (!selected) return [];
        const stops = Array.isArray(selected?.charging_stop_tou) ? selected.charging_stop_tou : [];
        const byKey = new Map();
        stops.forEach((s) => {
            const utility = s?.utility_region || s?.tou_rate_utility_name || 'Unknown utility';
            const sourceUtility = s?.tou_rate_utility_name || null;
            const touName = s?.tou_rate_name || (s?.tou_is_default ? 'DEFAULT_TOU' : 'Mapped TOU');
            const tou = Array.isArray(s?.tou) ? s.tou : [];
            const key = `${utility}|||${sourceUtility || ''}|||${touName}|||${formatTouPeriodLabel(tou)}`;
            if (!byKey.has(key)) {
                byKey.set(key, {
                    utility,
                    sourceUtility,
                    touName,
                    tou,
                    chartData: touToChartData(tou),
                    count: 0,
                });
            }
            byKey.get(key).count += 1;
        });
        return Array.from(byKey.values()).sort((a, b) => {
            if (a.utility !== b.utility) return a.utility.localeCompare(b.utility);
            return a.touName.localeCompare(b.touName);
        });
    }, [simulationResults?.results, selectedMapRowIndex]);

    const evForecast = evForecastByYear ?? DEFAULT_EV_FORECAST;
    const yearSnapshots = Array.isArray(simulationResults?.year_snapshots) ? simulationResults.year_snapshots : [];
    const selectedForecastSnapshot = useMemo(() => {
        if (!yearSnapshots.length) return null;
        const selectedYearNum = Number(selectedForecastYear);
        if (Number.isFinite(selectedYearNum)) {
            const hit = yearSnapshots.find((s) => Number(s?.year) === selectedYearNum);
            if (hit) return hit;
        }
        return yearSnapshots[yearSnapshots.length - 1];
    }, [yearSnapshots, selectedForecastYear]);
    const mapHexLoadAggregate = selectedForecastSnapshot?.hex_load_aggregate ?? simulationResults?.hex_load_aggregate ?? [];
    const simulationProgressPercent = useMemo(() => {
        if (!loading) return 0;
        // Indeterminate-style progress: climbs quickly then slows, capped until request finishes.
        return Math.min(95, Math.round(12 + simulationElapsedSec * 2.2));
    }, [loading, simulationElapsedSec]);
    const forecastProgressRows = useMemo(() => {
        const targets = Array.isArray(simulationResults?.forecast_targets) ? simulationResults.forecast_targets : [];
        if (!targets.length) return [];
        const snapshotByYear = new Map(
            yearSnapshots.map((snap) => [Number(snap?.year), snap])
        );
        const currentFeasible = Array.isArray(simulationResults?.results) ? simulationResults.results.length : 0;
        return targets.map((t) => {
            const year = Number(t?.year);
            const target = Number(t?.target_ev_count) || 0;
            const snap = snapshotByYear.get(year);
            const count = snap ? Number(snap?.feasible_count) || 0 : currentFeasible;
            const progress = target > 0 ? Math.max(0, Math.min(100, (count / target) * 100)) : 0;
            return {
                year,
                target,
                feasibleCount: count,
                reached: Boolean(snap),
                progress,
            };
        });
    }, [simulationResults?.forecast_targets, simulationResults?.results, yearSnapshots]);

    useEffect(() => {
        if (!loading) {
            setSimulationElapsedSec(0);
            return;
        }
        const intervalId = window.setInterval(() => {
            setSimulationElapsedSec((prev) => prev + 1);
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [loading]);

    const setEvForecastYear = useCallback((index, value) => {
        const n = clampEvCount(value);
        setEvForecastByYear((prev) => {
            const next = [...prev];
            next[index] = n;
            return next;
        });
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadUtilityOptions = async () => {
            setUtilitySearchLoading(true);
            try {
                const response = await axios.get(API_ENDPOINTS.getDefaultTouUtilityNames, {
                    headers: {
                        Authorization: `Token ${localStorage.getItem('authToken')}`,
                    },
                    params: {
                        limit: 5000,
                    },
                });
                if (cancelled) return;
                const utilityNames = Array.isArray(response.data?.utility_names)
                    ? response.data.utility_names
                    : [];
                setMdhdForecastUtilities(utilityNames);
                setUtilitySearchResults(utilityNames.slice(0, 200));
            } catch (err) {
                if (cancelled) return;
                logger.warn('Failed to load utility search options', err);
                setMdhdForecastUtilities([]);
                setUtilitySearchResults([]);
            } finally {
                if (!cancelled) {
                    setUtilitySearchLoading(false);
                }
            }
        };

        loadUtilityOptions();
        return () => {
            cancelled = true;
        };
    }, []);

    // Search utility options from default TOU mapping utility names (single source of truth).
    const searchUtilities = useCallback((query) => {
        const q = (query || '').trim().toLowerCase();
        if (!q) {
            setUtilitySearchResults(mdhdForecastUtilities.slice(0, 200));
            return;
        }
        const filtered = mdhdForecastUtilities.filter((u) => u.toLowerCase().includes(q)).slice(0, 200);
        setUtilitySearchResults(filtered);
    }, [mdhdForecastUtilities]);

    useEffect(() => {
        const t = setTimeout(() => searchUtilities(utilitySearchQuery), 300);
        return () => clearTimeout(t);
    }, [utilitySearchQuery, searchUtilities]);

    const addUtility = useCallback((name) => {
        if (name && !selectedUtilities.includes(name)) {
            setSelectedUtilities((prev) => [...prev, name].sort());
            setUtilitySearchQuery('');
            setUtilitySearchResults([]);
        }
    }, [selectedUtilities]);

    const removeUtility = useCallback((name) => {
        setSelectedUtilities((prev) => prev.filter((u) => u !== name));
    }, []);

    const toggleSeason = useCallback((season) => {
        setSelectedSeasons((prev) => (
            prev.includes(season)
                ? prev.filter((value) => value !== season)
                : [...prev, season]
        ));
    }, []);

    const buildSimulationRequestData = useCallback(() => {
        const targetEvCountFinal = Number(evForecast[evForecast.length - 1]) || 1000;
        const requestData = {
            mode: mode,
            depot_charging_power: Number(depotChargingPower) || 50,
            enroute_charging_power: Number(enrouteChargingPower) || 350,
            target_ev_count: targetEvCountFinal,
            soc_max: Number.isFinite(Number(socMax)) ? Number(socMax) : 0.95,
            soc_min: Number.isFinite(Number(socMin)) ? Number(socMin) : 0.10,
            soc_thres: Number.isFinite(Number(socThres)) ? Number(socThres) : 0.30,
            soc_initial: Number.isFinite(Number(socInitial)) ? Number(socInitial) : Number(socMax) || 0.95,
            batt_cap: [100, 200, 300, 400, 500, 600],
            is_depot: true,
            hexagon_has_charging: true,
            ev_forecast: FORECAST_YEARS.reduce((acc, y, i) => ({ ...acc, [y]: evForecast[i] }), {}),
        };
        if (selectedUtilities.length > 0) {
            requestData.utility_filter = selectedUtilities;
        }
        if (selectedSeasons.length > 0 && selectedSeasons.length < SEASON_OPTIONS.length) {
            requestData.season_filter = selectedSeasons;
        }
        return requestData;
    }, [
        depotChargingPower,
        enrouteChargingPower,
        evForecast,
        mode,
        selectedSeasons,
        selectedUtilities,
        socInitial,
        socMax,
        socMin,
        socThres,
    ]);

    const handleRunSimulation = async () => {
        setLoading(true);
        setError(null);
        setSimulationResults(null);

        try {
            const requestData = buildSimulationRequestData();
            logger.log('Running simulation with params:', requestData);
            logger.log("Sending API request to: ", API_ENDPOINTS.stopChainSimulation);
            const response = await axios.post(
                API_ENDPOINTS.stopChainSimulation,
                requestData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${localStorage.getItem('authToken')}`
                    }
                }
            );

            logger.log('Simulation results:', response.data);
            setSimulationResults(response.data);
            const snapshots = Array.isArray(response.data?.year_snapshots) ? response.data.year_snapshots : [];
            if (snapshots.length > 0) {
                const finalSnapshot = snapshots[snapshots.length - 1];
                setSelectedForecastYear(String(finalSnapshot?.year ?? ''));
            } else {
                setSelectedForecastYear('');
            }
            if (response.data?.results?.length > 0) {
                setShowResultsPage(true);
                setSelectedMapRowIndex(0);
                setExpandedRowIndex(0);
            }
        } catch (err) {
            logger.error('Error running simulation:', err);
            setError(err.response?.data?.error || err.message || 'Failed to run simulation');
        } finally {
            setLoading(false);
        }
    };

    const handleRunBenchmark = useCallback(async (benchmarkType, runMode = 'legacy_benchmark') => {
        setBenchmarkLoading(true);
        setBenchmarkError(null);
        setBenchmarkResults(null);
        setBenchmarkRunMode(runMode);
        try {
            const requestData = {
                ...buildSimulationRequestData(),
                benchmark_year_limit: Number(benchmarkYearLimit) || 1,
                benchmark_week_limit: Number(benchmarkWeekLimit) || 4,
                benchmark_parallel_workers: Number(benchmarkParallelWorkers) || 1,
                output_mode: 'hex_forecast_only',
                persist_result: false,
            };
            if (benchmarkType) {
                requestData.benchmark_type = benchmarkType;
            }
            logger.log('Running legacy benchmark with params:', requestData);
            const endpoint = runMode === 'legacy_rust_pointer'
                ? API_ENDPOINTS.stopChainSimulationLegacyRustPointer
                : API_ENDPOINTS.stopChainSimulationLegacyBenchmark;
            const response = await axios.post(
                endpoint,
                requestData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${localStorage.getItem('authToken')}`
                    }
                }
            );
            logger.log('Legacy benchmark results:', response.data);
            setBenchmarkResults(response.data);
        } catch (err) {
            logger.error('Error running legacy benchmark:', err);
            setBenchmarkError(err.response?.data?.error || err.message || 'Failed to run benchmark');
        } finally {
            setBenchmarkLoading(false);
        }
    }, [benchmarkParallelWorkers, benchmarkWeekLimit, benchmarkYearLimit, buildSimulationRequestData]);

    const handleBackToOptions = () => {
        setShowResultsPage(false);
        setSimulationResults(null);
        setExpandedRowIndex(null);
        setSelectedMapRowIndex(0);
        setSelectedForecastYear('');
        setShowChargersOnMap(false);
        setChargingStations([]);
        setChargingStationsLoading(false);
    };

    // Fetch public charging stations when "Show public chargers" is on.
    useEffect(() => {
        if (!showChargersOnMap || !showResultsPage || !simulationResults?.results?.length) {
            if (!showChargersOnMap) setChargingStations([]);
            return;
        }
        const selectedRow = simulationResults.results[selectedMapRowIndex];
        const stops = selectedRow?.stops || [];
        const params = { limit: 200 };
        const territoryIds = Array.isArray(simulationResults?.map_context?.utility_territory_ids)
            ? simulationResults.map_context.utility_territory_ids
            : [];
        if (territoryIds.length > 0) {
            params.utility_territory_id = territoryIds[0];
        } else if (stops.length > 0) {
            const lats = stops.map((s) => Number(s.lat ?? s.latitude ?? 0)).filter((n) => !Number.isNaN(n));
            const lngs = stops.map((s) => Number(s.lng ?? s.longitude ?? s.lon ?? 0)).filter((n) => !Number.isNaN(n));
            if (lats.length && lngs.length) {
                const pad = 0.05;
                const south = Math.min(...lats) - pad;
                const north = Math.max(...lats) + pad;
                const west = Math.min(...lngs) - pad;
                const east = Math.max(...lngs) + pad;
                params.bounds = `${south},${west},${north},${east}`;
            }
        }

        let cancelled = false;
        setChargingStationsLoading(true);
        axios
            .get(API_ENDPOINTS.getEvChargingStations, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`,
                },
                params,
            })
            .then((res) => {
                if (cancelled) return;
                const list = res.data?.stations ?? res.data?.features ?? [];
                setChargingStations(Array.isArray(list) ? list : []);
            })
            .catch((err) => {
                if (!cancelled) {
                    logger.warn('Failed to fetch charging stations', err);
                    setChargingStations([]);
                }
            })
            .finally(() => {
                if (!cancelled) setChargingStationsLoading(false);
            });
        return () => { cancelled = true; };
    }, [showChargersOnMap, showResultsPage, simulationResults?.results?.length, selectedMapRowIndex, simulationResults?.map_context?.utility_territory_ids]);

    // Results page: table left, map right, back button (fixed heights + scrollable content)
    if (showResultsPage && simulationResults?.results?.length > 0) {
        const results = simulationResults.results;
        const selectedRow = results[selectedMapRowIndex];
        const mapStops = selectedRow?.stops || [];

        return (
            <div
                className="fixed inset-y-0 right-0 flex flex-col overflow-hidden bg-gray-50"
                style={{ left: 'var(--app-sidebar-offset, 0px)' }}
            >
                    <div className="flex-1 flex min-h-0 overflow-hidden">
                        <div className="w-full max-w-xl lg:max-w-2xl flex flex-col min-h-0 border-r border-gray-200 bg-white shrink-0 overflow-hidden">
                            <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
                                <button
                                    type="button"
                                    onClick={handleBackToOptions}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition"
                                >
                                    <FaArrowLeft size={14} />
                                    Back to options
                                </button>
                                <span className="text-sm text-gray-600">
                                    {results.length} stop chain(s)
                                </span>
                            </div>
                            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto p-4 gap-4 pr-2">
                                {/* {simulationResults.simulation_params?.tou != null && simulationResults.simulation_params.tou.length > 0 && touChartData != null && (
                                    <div className="shrink-0 border rounded-lg overflow-hidden bg-white">
                                        <div className="px-3 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-800">
                                            {simulationResults.simulation_params?.tou_rate_name
                                                ? `TOU schedule: ${simulationResults.simulation_params.tou_rate_name}`
                                                : 'TOU schedule'}
                                        </div>
                                        <div className="p-2">
                                            <ResponsiveContainer width="100%" height={100}>
                                                <ComposedChart data={touChartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                                    <XAxis dataKey="hour" type="number" domain={[0, 24]} tick={{ fontSize: 9 }} tickCount={7} />
                                                    <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v}`} />
                                                    <Line type="stepBefore" dataKey="price" stroke="#2563eb" strokeWidth={1.5} dot={false} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )} */}
                                <div className="shrink-0 flex flex-col border rounded-lg overflow-hidden bg-white min-h-0">
                                    <div className="px-4 py-2 bg-gray-50 border-b text-sm font-semibold text-gray-800 shrink-0">Results by vehicle-week</div>
                                    <div className="h-[220px] overflow-auto shrink-0">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50 sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">#</th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">Week</th>
                                                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">Truck</th>
                                                    <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 uppercase">Stops</th>
                                                    <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 uppercase">Cost</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                                {results.map((row, idx) => (
                                                    <tr
                                                        key={idx}
                                                        onClick={() => { setSelectedMapRowIndex(idx); setExpandedRowIndex(idx); }}
                                                        className={`cursor-pointer ${selectedMapRowIndex === idx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                    >
                                                        <td className="px-3 py-1.5 text-gray-700">{idx + 1}</td>
                                                        <td className="px-3 py-1.5 text-gray-800">{row.week ?? '—'}</td>
                                                        <td className="px-3 py-1.5 font-mono text-xs text-gray-800">{row.truck_id ?? row.truck_file ?? '—'}</td>
                                                        <td className="px-3 py-1.5 text-right text-gray-800">{row.num_stops ?? '—'}</td>
                                                        <td className="px-3 py-1.5 text-right text-gray-800">{row.total_cost != null ? `$${Number(row.total_cost).toFixed(2)}` : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col border rounded-lg overflow-hidden bg-white min-h-0">
                                    <div className="px-4 py-2 bg-gray-50 border-b text-sm font-semibold text-gray-800 shrink-0">
                                        TOU information (selected route)
                                    </div>
                                    <div className="overflow-x-auto">
                                        {selectedRouteTouRows.length === 0 ? (
                                            <div className="px-4 py-6 text-sm text-center text-gray-500">No TOU data for selected route.</div>
                                        ) : (
                                            <table className="w-full table-fixed divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-50 sticky top-0 z-10">
                                                    <tr>
                                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">Utility</th>
                                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">TOU</th>
                                                        <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 uppercase">Used at stops</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                    {selectedRouteTouRows.map((touRow, idx) => (
                                                        <React.Fragment key={`selected-${touRow.utility}-${touRow.touName}-${idx}`}>
                                                            <tr>
                                                                <td className="px-3 py-2 text-gray-800 align-top break-words whitespace-normal">{touRow.utility}</td>
                                                                <td className="px-3 py-2 text-gray-700 align-top">
                                                                    <div className="font-medium break-words whitespace-normal">{touRow.touName}</div>
                                                                    {touRow.sourceUtility && !utilitiesEquivalent(touRow.sourceUtility, touRow.utility) && (
                                                                        <div className="text-[11px] mt-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                                                            Municipality uses TOU from: {touRow.sourceUtility}
                                                                        </div>
                                                                    )}
                                                                    <div className="text-[11px] text-gray-500 mt-1 break-words whitespace-normal">{formatTouPeriodLabel(touRow.tou)}</div>
                                                                </td>
                                                                <td className="px-3 py-2 text-right text-gray-700 align-top">{touRow.count}</td>
                                                            </tr>
                                                            <tr>
                                                                <td colSpan={3} className="px-3 pb-3 pt-0">
                                                                    <div className="border border-gray-200 rounded bg-gray-50 px-3 py-2">
                                                                        <p className="text-xs font-medium text-gray-700 mb-1">TOU schedule: {touRow.touName}</p>
                                                                        <div style={{ width: '100%', height: 90 }}>
                                                                            <ResponsiveContainer width="100%" height="100%">
                                                                                <ComposedChart data={touRow.chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                                                                    <XAxis dataKey="hour" type="number" domain={[0, 24]} tick={{ fontSize: 8 }} tickCount={7} />
                                                                                    <YAxis tick={{ fontSize: 8 }} tickFormatter={(v) => `$${v}`} />
                                                                                    <Tooltip
                                                                                        content={({ active, payload }) => {
                                                                                            if (!active || !payload?.length) return null;
                                                                                            const d = payload[0]?.payload;
                                                                                            return d ? (
                                                                                                <div className="bg-white border border-gray-200 rounded shadow px-2 py-1 text-xs">
                                                                                                    Hour {d.hour}: ${d.price != null ? Number(d.price).toFixed(4) : '—'}/kWh
                                                                                                </div>
                                                                                            ) : null;
                                                                                        }}
                                                                                    />
                                                                                    <Line type="stepBefore" dataKey="price" stroke="#7c3aed" strokeWidth={1.5} dot={false} />
                                                                                </ComposedChart>
                                                                            </ResponsiveContainer>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col border rounded-lg overflow-hidden bg-white min-h-0">
                                    <div className="px-4 py-2 bg-gray-50 border-b text-sm font-semibold text-gray-800 shrink-0">TOUs used in this simulation</div>
                                    <div className="overflow-x-auto">
                                        {touUsageRows.length === 0 ? (
                                            <div className="px-4 py-8 text-sm text-center text-gray-500">No TOU usage data available.</div>
                                        ) : (
                                            <table className="w-full table-fixed divide-y divide-gray-200 text-sm">
                                                <thead className="bg-gray-50 sticky top-0 z-10">
                                                    <tr>
                                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">Utility</th>
                                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 uppercase">TOU</th>
                                                        <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 uppercase">Used at stops</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                    {touUsageRows.map((touRow, idx) => (
                                                        <React.Fragment key={`${touRow.utility}-${touRow.touName}-${idx}`}>
                                                            <tr>
                                                                <td className="px-3 py-2 text-gray-800 align-top break-words whitespace-normal">{touRow.utility}</td>
                                                                <td className="px-3 py-2 text-gray-700 align-top">
                                                                    <div className="font-medium break-words whitespace-normal">{touRow.touName}</div>
                                                                    {touRow.sourceUtility && !utilitiesEquivalent(touRow.sourceUtility, touRow.utility) && (
                                                                        <div className="text-[11px] mt-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                                                            Municipality uses TOU from: {touRow.sourceUtility}
                                                                        </div>
                                                                    )}
                                                                    <div className="text-[11px] text-gray-500 mt-1 break-words whitespace-normal">{formatTouPeriodLabel(touRow.tou)}</div>
                                                                </td>
                                                                <td className="px-3 py-2 text-right text-gray-700 align-top">{touRow.count}</td>
                                                            </tr>
                                                            <tr>
                                                                <td colSpan={3} className="px-3 pb-3 pt-0">
                                                                    <div className="border border-gray-200 rounded bg-gray-50 px-3 py-2">
                                                                        <p className="text-xs font-medium text-gray-700 mb-1">
                                                                            TOU schedule: {touRow.touName}
                                                                        </p>
                                                                        <div style={{ width: '100%', height: 90 }}>
                                                                            <ResponsiveContainer width="100%" height="100%">
                                                                                <ComposedChart data={touRow.chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                                                                    <XAxis dataKey="hour" type="number" domain={[0, 24]} tick={{ fontSize: 8 }} tickCount={7} />
                                                                                    <YAxis tick={{ fontSize: 8 }} tickFormatter={(v) => `$${v}`} />
                                                                                    <Tooltip
                                                                                        content={({ active, payload }) => {
                                                                                            if (!active || !payload?.length) return null;
                                                                                            const d = payload[0]?.payload;
                                                                                            return d ? (
                                                                                                <div className="bg-white border border-gray-200 rounded shadow px-2 py-1 text-xs">
                                                                                                    Hour {d.hour}: ${d.price != null ? Number(d.price).toFixed(4) : '—'}/kWh
                                                                                                </div>
                                                                                            ) : null;
                                                                                        }}
                                                                                    />
                                                                                    <Line type="stepBefore" dataKey="price" stroke="#7c3aed" strokeWidth={1.5} dot={false} />
                                                                                </ComposedChart>
                                                                            </ResponsiveContainer>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                                {selectedRow && (
                                    <div className="shrink-0 flex flex-col border rounded-lg overflow-hidden bg-gray-50 min-h-0">
                                        <div className="px-3 py-1.5 bg-gray-100 border-b text-xs font-semibold text-gray-800 shrink-0">
                                            Detail for selected route
                                        </div>
                                        <div className="max-h-[420px] overflow-y-auto p-3">
                                            <RowDetailContent row={results[expandedRowIndex] || selectedRow} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 min-w-0 min-h-0 flex flex-col relative overflow-hidden">
                            <div className="absolute top-2 right-6 z-[10] flex flex-wrap items-center gap-3 bg-white/95 border border-gray-200 rounded-lg shadow-sm px-3 py-2">
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                                    <input
                                        type="checkbox"
                                        checked={showChargersOnMap}
                                        onChange={(e) => setShowChargersOnMap(e.target.checked)}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    <span>Show public chargers</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                                    <input
                                        type="checkbox"
                                        checked={showLoadHeatmap}
                                        onChange={(e) => setShowLoadHeatmap(e.target.checked)}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    <span>Show load heatmap (H3)</span>
                                </label>
                                {yearSnapshots.length > 0 && (
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                        <span>Heatmap year</span>
                                        <select
                                            value={selectedForecastSnapshot?.year != null ? String(selectedForecastSnapshot.year) : ''}
                                            onChange={(e) => setSelectedForecastYear(e.target.value)}
                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {yearSnapshots.map((snap) => (
                                                <option key={snap.year} value={String(snap.year)}>
                                                    {snap.year} ({snap.feasible_count}/{snap.target_ev_count})
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                    <span>Heatmap layer</span>
                                    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden bg-white">
                                        <button
                                            type="button"
                                            onClick={() => setHeatmapLayerMetric('energy_delivered')}
                                            className={`px-2.5 py-1 text-xs font-medium ${heatmapLayerMetric === 'energy_delivered' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                        >
                                            Energy
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setHeatmapLayerMetric('peak_power_kw')}
                                            className={`px-2.5 py-1 text-xs font-medium border-l border-gray-300 ${heatmapLayerMetric === 'peak_power_kw' ? 'bg-violet-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                                        >
                                            Peak power
                                        </button>
                                    </div>
                                </div>
                                {chargingStationsLoading && (
                                    <span className="text-xs text-gray-500">Loading…</span>
                                )}
                                {simulationResults?.map_context?.utility_territory_names?.length > 0 && (
                                    <span className="text-xs text-gray-500">
                                        Utility boundary: {simulationResults.map_context.utility_territory_names.join(', ')}
                                    </span>
                                )}
                            </div>
                            <StopChainMap
                                stops={mapStops}
                                selectedStopIndex={0}
                                chargingStations={chargingStations.slice(0, 100)}
                                showChargerMarkers={showChargersOnMap}
                                hexLoadAggregate={mapHexLoadAggregate}
                                showLoadHeatmap={showLoadHeatmap}
                                loadHeatmapMetric={heatmapLayerMetric}
                                utilityBoundaryGeometry={simulationResults?.map_context?.utility_boundary_geometry || null}
                                showUtilityBoundary={Boolean(simulationResults?.map_context?.utility_boundary_geometry)}
                                className="w-full flex-1"
                            />
                        </div>
                    </div>
            </div>
        );
    }

    // Input form page
    return (
        <div className="min-h-screen bg-gray-50">
                <div className="p-8 max-w-5xl mx-auto">
                    <div
                        ref={cardRef}
                        className={`bg-white rounded-xl shadow-lg p-8 mt-[3vh] transition-all duration-700 ease-out transform ${
                            isCardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}
                    >
                        <header className="flex flex-col gap-3 mb-8">
                            <p className="text-sm uppercase tracking-wide text-blue-600 font-semibold flex items-center gap-2">
                                <FaPlayCircle size={18} />
                                Vehicle Simulation
                            </p>
                            <h1 className="text-3xl font-bold text-gray-900 font-ui">
                                Stop Chain Simulation & Aggregation
                            </h1>
                            <p className="text-gray-600">
                                Configure charging behavior and TOU pricing to drive the stop-chain simulation.
                            </p>
                        </header>

                        <div className="space-y-6">
                            <div className="border rounded-lg p-6 bg-gray-50">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Mode of charging
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {[
                                        { id: 'unmanaged', label: 'Unmanaged' },
                                        { id: 'passive', label: 'Passive managed' },
                                        { id: 'active', label: 'Active managed' },
                                    ].map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => setMode(option.id)}
                                            className={`w-full border rounded-lg px-4 py-3 text-left transition ${
                                                mode === option.id
                                                    ? 'border-blue-600 bg-blue-50 text-blue-800'
                                                    : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-700">
                                                    <LuCar size={18} />
                                                </span>
                                                <div>
                                                    <p className="font-semibold">{option.label}</p>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="border rounded-lg p-6 bg-white">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Utility filter
                                </label>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Filter by utilities (optional)</label>
                                        <p className="text-xs text-gray-500 mb-2">
                                            If blank, sample from all stop chains. If selected, only sample chains with at least one stop in these utilities. TOU is resolved per stop from the utility at that stop.
                                        </p>
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {selectedUtilities.map((u) => (
                                                <span
                                                    key={u}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 text-sm"
                                                >
                                                    {u}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeUtility(u)}
                                                        className="ml-0.5 hover:text-blue-600 focus:outline-none"
                                                        aria-label={`Remove ${u}`}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={utilitySearchQuery}
                                                onChange={(e) => setUtilitySearchQuery(e.target.value)}
                                                placeholder="Search utilities (default TOU naming)..."
                                                className="w-full rounded-lg border border-gray-200 p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            {utilitySearchLoading && (
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">Loading…</span>
                                            )}
                                            {utilitySearchQuery.length >= 1 && utilitySearchResults.length > 0 && (
                                                <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                                                    {utilitySearchResults
                                                        .filter((r) => !selectedUtilities.includes(r))
                                                        .map((r) => (
                                                            <li key={r}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => addUtility(r)}
                                                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-gray-800"
                                                                >
                                                                    {r}
                                                                </button>
                                                            </li>
                                                        ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border rounded-lg p-6 bg-white">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Season filter
                                </label>
                                <p className="text-xs text-gray-500 mb-3">
                                    Filter vehicle-weeks by the season of their first stop arrival month. Leave all selected to sample all seasons.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {SEASON_OPTIONS.map(([value, label]) => {
                                        const isSelected = selectedSeasons.includes(value);
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => toggleSeason(value)}
                                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                                                    isSelected
                                                        ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {selectedSeasons.length === 0 && (
                                    <p className="text-xs text-amber-600 mt-3">
                                        No seasons selected. The backend will treat this as no season filter.
                                    </p>
                                )}
                                {simulationResults?.debug_info?.season_counts && (
                                    <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
                                        <div className="text-xs font-semibold text-gray-700 mb-2">Debug season counts</div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-700">
                                            {SEASON_OPTIONS.map(([value, label]) => (
                                                <div key={`debug-${value}`} className="rounded bg-white border border-gray-200 px-2 py-1.5">
                                                    <div className="text-gray-500">{label}</div>
                                                    <div className="font-semibold text-gray-800">
                                                        {simulationResults.debug_info.season_counts?.[value] ?? 0}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="border rounded-lg p-4 bg-white">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Charging power (kW)
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Depot charging power
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={2000}
                                            step={1}
                                            value={depotChargingPower}
                                            onChange={(e) => setDepotChargingPower(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            En-route charging power
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={2000}
                                            step={1}
                                            value={enrouteChargingPower}
                                            onChange={(e) => setEnrouteChargingPower(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="350"
                                        />
                                    </div>
                                </div>
                            </div>

                            <DndProvider backend={HTML5Backend}>
                                <div className="border rounded-lg p-4 bg-white w-full min-w-0">
                                    <label className="block text-sm font-semibold text-gray-800 mb-3">Target EV count forecast</label>
                                    <p className="text-xs text-gray-500 mb-3">Drag points vertically or use inputs below. Simulation runs to the final-year target and captures yearly milestones. Values: 0–100,000 in steps of 50.</p>
                                    <div className="mb-4 w-full min-w-0" style={{ width: '100%' }}>
                                        <ForecastChartDnd
                                            values={evForecast}
                                            onValueChange={setEvForecastYear}
                                            years={FORECAST_YEARS}
                                            label="EVs"
                                            color="#2563eb"
                                        />
                                    </div>
                                    <div className="grid grid-cols-4 gap-2">
                                        {FORECAST_YEARS.map((year, i) => (
                                            <div key={year}>
                                                <label className="block text-xs font-medium text-gray-600 mb-0.5">{year}</label>
                                                <input
                                                    type="number"
                                                    min={EV_COUNT_MIN}
                                                    max={EV_COUNT_MAX}
                                                    step={EV_COUNT_STEP}
                                                    value={evForecast[i]}
                                                    onChange={(e) => setEvForecastYear(i, e.target.value)}
                                                    className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </DndProvider>

                            <div className="border rounded-lg p-4 bg-white">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">State of charge thresholds</label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Initial SoC (0–1)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={socInitial}
                                            onChange={(e) => setSocInitial(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0.95"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Start of week</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Maximum SoC (0–1)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={socMax}
                                            onChange={(e) => setSocMax(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0.95"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Max charge level</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Minimum SoC (0–1)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={socMin}
                                            onChange={(e) => setSocMin(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0.10"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Infeasible below</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">SoC threshold (0–1)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={socThres}
                                            onChange={(e) => setSocThres(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0.30"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Charge trigger (enroute)</p>
                                    </div>
                                </div>
                            </div>

                            <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                                <div className="flex flex-col gap-2 mb-4">
                                    <label className="block text-sm font-semibold text-gray-800">
                                        Legacy benchmark controls
                                    </label>
                                    <p className="text-xs text-gray-600">
                                        These tests run separately from the main workflow and are intended for analytical comparisons only.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Benchmark years</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={FORECAST_YEARS.length}
                                            step={1}
                                            value={benchmarkYearLimit}
                                            onChange={(e) => setBenchmarkYearLimit(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Weeks per year</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={52}
                                            step={1}
                                            value={benchmarkWeekLimit}
                                            onChange={(e) => setBenchmarkWeekLimit(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parallel workers</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={64}
                                            step={1}
                                            value={benchmarkParallelWorkers}
                                            onChange={(e) => setBenchmarkParallelWorkers(e.target.value)}
                                            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => handleRunBenchmark('serial_language_test')}
                                        disabled={loading || benchmarkLoading}
                                        className="px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold shadow-sm hover:bg-amber-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {benchmarkLoading ? 'Running benchmark...' : 'Serial language test'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleRunBenchmark('serial_parallel_test')}
                                        disabled={loading || benchmarkLoading}
                                        className="px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold shadow-sm hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {benchmarkLoading ? 'Running benchmark...' : 'Serial vs parallel test'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleRunBenchmark(null, 'legacy_rust_pointer')}
                                        disabled={loading || benchmarkLoading}
                                        className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold shadow-sm hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {benchmarkLoading ? 'Running benchmark...' : 'Rust pointer test'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleRunSimulation}
                                        disabled={loading || benchmarkLoading}
                                        className="px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold shadow-sm hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <LoadingIndicator size="small" />
                                                <span>Running simulation...</span>
                                            </>
                                        ) : (
                                            'Run simulation'
                                        )}
                                    </button>
                                </div>
                                {benchmarkError && (
                                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-red-800 text-sm font-semibold">Benchmark error</p>
                                        <p className="text-red-600 text-sm mt-1">{benchmarkError}</p>
                                    </div>
                                )}
                                {benchmarkResults && (
                                    <div className="mt-4 space-y-4">
                                        <div className="rounded-lg border border-amber-200 bg-white p-4">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {benchmarkRunMode === 'legacy_rust_pointer'
                                                            ? 'Rust pointer test'
                                                            : benchmarkResults?.benchmark_type === 'serial_parallel_test'
                                                                ? 'Serial vs parallel test'
                                                                : 'Serial language test'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        API runtime: {formatBenchmarkSeconds(benchmarkResults?.api_elapsed_sec)}
                                                    </p>
                                                    <p className="text-[11px] text-gray-400 mt-1">
                                                        Whole benchmark request wall time, including Python orchestration, validation, and any Rust payload/runner overhead.
                                                    </p>
                                                </div>
                                                <div className="text-xs text-gray-600">
                                                    Scope: {benchmarkResults?.benchmark_scope?.forecast_years?.join(', ') || '—'}
                                                    {' | '}
                                                    {benchmarkResults?.benchmark_scope?.week_limit ?? '—'} week(s)/year
                                                </div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-600">
                                                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                                                    Candidate pool: {benchmarkResults?.benchmark_scope?.candidate_stop_chain_count ?? '—'}
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                                                    Rust chunk size: {benchmarkResults?.rust_payload?.chunk_size ?? benchmarkResults?.benchmark_scope?.candidate_chain_limit ?? '1000'}
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                                                    Rust tasks: {benchmarkResults?.rust_payload?.task_count ?? '—'}
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                                                    Rust payload: {formatBytes(benchmarkResults?.rust_payload?.payload_size_bytes)}
                                                </div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-700">
                                                <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                                                    Python payload build: {formatBenchmarkSeconds(benchmarkResults?.candidate?.runtime_breakdown?.python_payload_build_sec)}
                                                </div>
                                                <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                                                    Runner wall time: {formatBenchmarkSeconds(benchmarkResults?.candidate?.runtime_breakdown?.runner_wall_sec || benchmarkResults?.candidate?.runner_wall_sec)}
                                                </div>
                                                <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                                                    Rust binary runtime: {formatBenchmarkSeconds(benchmarkResults?.candidate?.runtime_breakdown?.rust_binary_runtime_sec || benchmarkResults?.candidate?.runtime_sec)}
                                                </div>
                                                <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                                                    Endpoint wall time: {formatBenchmarkSeconds(benchmarkResults?.candidate?.runtime_breakdown?.endpoint_wall_sec || benchmarkResults?.candidate?.endpoint_wall_sec)}
                                                </div>
                                            </div>
                                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                                    <div className="text-xs uppercase text-gray-500">
                                                        {benchmarkResults?.baseline?.label || 'Python serial'}
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-800 mt-1">
                                                        Python reference run
                                                    </div>
                                                    <div className="text-sm text-gray-700 mt-2">
                                                        Runtime: {formatBenchmarkSeconds(benchmarkResults?.baseline?.runtime_sec)}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Scope: {benchmarkResults?.baseline?.runtime_scope || '—'}
                                                    </div>
                                                    {benchmarkResults?.baseline?.runtime_notes && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {benchmarkResults.baseline.runtime_notes}
                                                        </div>
                                                    )}
                                                    <div className="text-sm text-gray-700">
                                                        Weeks: {benchmarkResults?.baseline?.summary?.week_count ?? '—'}
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        Feasible: {benchmarkResults?.baseline?.summary?.feasible_count ?? '—'}
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        Load: {benchmarkResults?.baseline?.summary?.total_load_mwh ?? '—'} MWh
                                                    </div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                                                    <div className="text-xs uppercase text-gray-500">
                                                        {benchmarkResults?.candidate?.label || benchmarkCandidateFallbackLabel}
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-800 mt-1">
                                                        Comparison run
                                                    </div>
                                                    <div className="text-sm text-gray-700 mt-2">
                                                        Status: {benchmarkResults?.candidate?.status || '—'}
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        Runtime: {formatBenchmarkSeconds(benchmarkResults?.candidate?.runtime_sec)}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Scope: {benchmarkResults?.candidate?.runtime_scope || '—'}
                                                    </div>
                                                    {benchmarkResults?.candidate?.runtime_notes && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {benchmarkResults.candidate.runtime_notes}
                                                        </div>
                                                    )}
                                                    <div className="text-sm text-gray-700">
                                                        Feasible: {benchmarkResults?.candidate?.summary?.feasible_count ?? '—'}
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        Load: {benchmarkResults?.candidate?.summary?.total_load_mwh ?? '—'} MWh
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-2">
                                                        Candidate chains sent to Rust: {benchmarkResults?.rust_payload?.candidate_chain_count ?? '—'}
                                                        {benchmarkResults?.rust_payload?.available_candidate_chain_count != null && (
                                                            <> / {benchmarkResults.rust_payload.available_candidate_chain_count} available</>
                                                        )}
                                                    </div>
                                                    {benchmarkResults?.candidate?.error && (
                                                        <div className="text-xs text-amber-700 mt-2">
                                                            {benchmarkResults.candidate.error}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                                                <div>
                                                    Validation:{' '}
                                                    {benchmarkResults?.validation
                                                        ? (benchmarkResults.validation.matches ? 'Exact match' : 'Mismatch')
                                                        : 'Unavailable'}
                                                </div>
                                                {!benchmarkResults?.validation?.matches && (
                                                    <div className="mt-2 text-xs text-amber-700">
                                                        A mismatch means the Rust result object does not exactly match the Python result after normalization. Common causes are algorithm differences, charging logic bugs, or Rust payload capping that sends fewer chains to Rust than Python evaluates.
                                                    </div>
                                                )}
                                                {benchmarkResults?.validation?.reference_digest && (
                                                    <div className="mt-1 break-all text-xs text-gray-500">
                                                        {(benchmarkResults?.baseline?.label || 'Python serial')} digest: {benchmarkResults.validation.reference_digest}
                                                    </div>
                                                )}
                                                {benchmarkResults?.validation?.candidate_digest && (
                                                    <div className="mt-1 break-all text-xs text-gray-500">
                                                        {(benchmarkResults?.candidate?.label || benchmarkCandidateFallbackLabel)} digest: {benchmarkResults.validation.candidate_digest}
                                                    </div>
                                                )}
                                                {benchmarkResults?.candidate?.metadata?.implementation_stage && (
                                                    <div className="mt-2 text-xs text-gray-500">
                                                        Rust stage: {benchmarkResults.candidate.metadata.implementation_stage}
                                                    </div>
                                                )}
                                            </div>
                                            {benchmarkResults?.comparison_details && (
                                                <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
                                                    <div className="text-sm font-semibold text-gray-800 mb-2">Comparison details</div>
                                                    {Object.keys(benchmarkResults.comparison_details?.field_differences || {}).length === 0 ? (
                                                        <div className="text-xs text-gray-600">No summary-level differences detected.</div>
                                                    ) : (
                                                        <div className="space-y-2 text-xs text-gray-700">
                                                            {Object.entries(benchmarkResults.comparison_details.field_differences).map(([field, detail]) => (
                                                                <div key={field} className="rounded border border-gray-200 bg-white p-2">
                                                                    <div className="font-medium text-gray-800">{field}</div>
                                                                    <div>{benchmarkResults.comparison_details?.left_label || 'Python'}: {String(detail?.left)}</div>
                                                                    <div>{benchmarkResults.comparison_details?.right_label || 'Rust'}: {String(detail?.right)}</div>
                                                                    <div>Delta: {detail?.delta != null ? String(detail.delta) : '—'}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {benchmarkResults?.trace_differences && (
                                                <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
                                                    <div className="text-sm font-semibold text-amber-900 mb-2">Trace differences</div>
                                                    <div className="text-xs text-amber-800">
                                                        Week traces match: {benchmarkResults.trace_differences?.weeks_match ? 'Yes' : 'No'}
                                                    </div>
                                                    {benchmarkResults.trace_differences?.first_mismatch && (
                                                        <div className="mt-2 rounded border border-amber-200 bg-white p-3 text-xs text-gray-700 space-y-1">
                                                            <div className="font-medium text-gray-800">
                                                                First mismatch at week position {benchmarkResults.trace_differences.first_mismatch.week_position}
                                                            </div>
                                                            <div>
                                                                {benchmarkResults.trace_differences?.left_label || 'Python'} week:{' '}
                                                                {benchmarkResults.trace_differences.first_mismatch.left_row?.week_label || '—'}
                                                            </div>
                                                            <div>
                                                                {benchmarkResults.trace_differences?.right_label || 'Rust'} week:{' '}
                                                                {benchmarkResults.trace_differences.first_mismatch.right_row?.week_label || '—'}
                                                            </div>
                                                            <div>
                                                                First processed-chain mismatch index:{' '}
                                                                {benchmarkResults.trace_differences.first_mismatch.first_processed_mismatch_at ?? '—'}
                                                            </div>
                                                            <div>
                                                                First feasible-chain mismatch index:{' '}
                                                                {benchmarkResults.trace_differences.first_mismatch.first_feasible_mismatch_at ?? '—'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="mt-4 space-y-3">
                                                <JsonDiffHighlighter
                                                    title="Highlighted normalized differences"
                                                    leftTitle={`${benchmarkResults?.baseline?.label || 'Python serial'} normalized`}
                                                    leftValue={benchmarkResults?.baseline?.normalized_result}
                                                    rightTitle={`${benchmarkResults?.candidate?.label || benchmarkCandidateFallbackLabel} normalized`}
                                                    rightValue={benchmarkResults?.candidate?.normalized_result}
                                                    defaultOpen={!benchmarkResults?.validation?.matches}
                                                />
                                                <SideBySideJsonComparison
                                                    title="Side-by-side exact result comparison"
                                                    leftTitle={benchmarkResults?.baseline?.label || 'Python serial'}
                                                    leftValue={benchmarkResults?.baseline?.result}
                                                    rightTitle={benchmarkResults?.candidate?.label || benchmarkCandidateFallbackLabel}
                                                    rightValue={benchmarkResults?.candidate?.result}
                                                    defaultOpen
                                                />
                                                <SideBySideJsonComparison
                                                    title="Side-by-side normalized result comparison"
                                                    leftTitle={`${benchmarkResults?.baseline?.label || 'Python serial'} normalized`}
                                                    leftValue={benchmarkResults?.baseline?.normalized_result}
                                                    rightTitle={`${benchmarkResults?.candidate?.label || benchmarkCandidateFallbackLabel} normalized`}
                                                    rightValue={benchmarkResults?.candidate?.normalized_result}
                                                />
                                                <JsonDetails title="Trace differences" value={benchmarkResults?.trace_differences} defaultOpen={!benchmarkResults?.validation?.matches} />
                                                <SideBySideJsonComparison
                                                    title="Side-by-side runtime breakdown"
                                                    leftTitle={`${benchmarkResults?.baseline?.label || 'Python serial'} runtime`}
                                                    leftValue={benchmarkResults?.baseline?.runtime_breakdown}
                                                    rightTitle={`${benchmarkResults?.candidate?.label || benchmarkCandidateFallbackLabel} runtime`}
                                                    rightValue={benchmarkResults?.candidate?.runtime_breakdown}
                                                />
                                                <JsonDetails title="Rust runner response" value={benchmarkResults?.candidate?.raw_response} />
                                                <JsonDetails title="Rust runner metadata" value={benchmarkResults?.candidate?.metadata} />
                                                <JsonDetails title="Rust benchmark input payload" value={benchmarkResults?.rust_payload?.input} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {loading && (
                                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                                    <div className="flex items-center justify-between text-xs text-blue-800 mb-2">
                                        <span>Simulation progress</span>
                                        <span>{simulationProgressPercent}%</span>
                                    </div>
                                    <div className="h-2 w-full rounded-full bg-blue-100 overflow-hidden">
                                        <div
                                            className="h-full bg-blue-600 transition-all duration-500 ease-out"
                                            style={{ width: `${simulationProgressPercent}%` }}
                                        />
                                    </div>
                                    <p className="text-[11px] text-blue-700 mt-2">
                                        Running routes... elapsed {simulationElapsedSec}s
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-800 text-sm font-semibold">Error</p>
                                    <p className="text-red-600 text-sm mt-1">{error}</p>
                                </div>
                            )}

                            {simulationResults && (
                                <div className="mt-6 space-y-4">
                                    <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                                        <p className="text-green-800 text-sm font-semibold mb-2">Simulation Complete</p>
                                        <div className="text-sm text-green-700 space-y-1">
                                            <p>Total processed: {simulationResults.total_processed}</p>
                                            <p>Total errors: {simulationResults.total_errors}</p>
                                            {simulationResults.results && (
                                                <p>Results: {simulationResults.results.length} vehicle-week(s) processed</p>
                                            )}
                                            {Array.isArray(simulationResults?.forecast_years_reached) && simulationResults.forecast_years_reached.length > 0 && (
                                                <p>Forecast milestones reached: {simulationResults.forecast_years_reached.join(', ')}</p>
                                            )}
                                            {Array.isArray(simulationResults?.forecast_years_pending) && simulationResults.forecast_years_pending.length > 0 && (
                                                <p>Forecast milestones pending: {simulationResults.forecast_years_pending.join(', ')}</p>
                                            )}
                                        </div>
                                    </div>
                                    {forecastProgressRows.length > 0 && (
                                        <div className="border rounded-lg overflow-hidden bg-white">
                                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                <h3 className="text-sm font-semibold text-gray-800">Forecast progress by year</h3>
                                            </div>
                                            <div className="p-4 space-y-3">
                                                {forecastProgressRows.map((row) => (
                                                    <div key={row.year} className="border rounded-lg p-3 bg-gray-50">
                                                        <div className="flex items-center justify-between text-sm mb-1.5">
                                                            <div className="font-semibold text-gray-800">{row.year}</div>
                                                            <div className="text-gray-700">
                                                                {row.feasibleCount} / {row.target} feasible
                                                            </div>
                                                        </div>
                                                        <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                                                            <div
                                                                className={`h-full transition-all duration-500 ${
                                                                    row.reached ? 'bg-green-600' : 'bg-blue-600'
                                                                }`}
                                                                style={{ width: `${row.progress}%` }}
                                                            />
                                                        </div>
                                                        <div className="mt-1 text-xs text-gray-600">
                                                            {row.reached ? 'Milestone reached' : 'Milestone pending'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {simulationResults.results && simulationResults.results.length > 0 && (
                                        <div className="border rounded-lg overflow-hidden bg-white">
                                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                <h3 className="text-sm font-semibold text-gray-800">Results by vehicle-week</h3>
                                            </div>
                                            <div className="h-[420px] overflow-auto">
                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                                        <tr>
                                                            <th className="w-8 px-1 py-2 text-center text-xs font-medium text-gray-500" aria-label="Expand" />
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Week</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Truck ID</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Truck file</th>
                                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Stops</th>
                                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Total cost ($)</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200 bg-white">
                                                        {simulationResults.results.map((row, idx) => (
                                                            <React.Fragment key={idx}>
                                                                <tr className="hover:bg-gray-50">
                                                                    <td className="w-8 px-1 py-2 text-center align-middle">
                                                                        {row.status === 'feasible' ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setExpandedRowIndex(expandedRowIndex === idx ? null : idx)}
                                                                                className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                                                                                aria-label={expandedRowIndex === idx ? 'Collapse' : 'Expand'}
                                                                            >
                                                                                {expandedRowIndex === idx ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                                                                            </button>
                                                                        ) : (
                                                                            <span className="w-4 inline-block" />
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-2 text-gray-800">{row.week ?? '—'}</td>
                                                                    <td className="px-4 py-2 text-gray-800 font-mono text-xs">{row.truck_id ?? '—'}</td>
                                                                    <td className="px-4 py-2 text-gray-600">{row.truck_file ?? '—'}</td>
                                                                    <td className="px-4 py-2 text-right text-gray-800">{row.num_stops ?? '—'}</td>
                                                                    <td className="px-4 py-2 text-right text-gray-800">
                                                                        {row.total_cost != null ? Number(row.total_cost).toFixed(2) : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                                                            row.status === 'feasible' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                                        }`}>
                                                                            {row.status ?? '—'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                {expandedRowIndex === idx && row.status === 'feasible' && (
                                                                    <tr>
                                                                        <td colSpan={7} className="px-4 py-3 bg-gray-50 border-b border-gray-200 align-top">
                                                                            <RowDetailContent row={row} />
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
        </div>
    );
}

export default UtilityVehicleSimulation;
